import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, throwError } from 'rxjs';

/** localStorage key the JWT is stored under (see api-key.interceptor). */
const TOKEN_NAME = 'appToken';

/**
 * On any 401 from the API, the session is no longer valid (absent, expired, or
 * rejected by the backend auth filter). Clear the token and bounce to login so
 * the user re-authenticates instead of seeing empty/broken pages.
 *
 * Skips the login/authenticate call itself so a bad-credentials 401 is handled
 * by the login screen rather than triggering a redirect loop.
 *
 * On any 403 from the API, the session is valid but the user's group lacks
 * access to the requested module. Show a non-fatal snackbar and re-throw so
 * the calling code's error handler still runs; do NOT clear the session.
 */
export const authErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const snackBar = inject(MatSnackBar);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const isAuthCall = req.url.includes('/authenticate') || req.url.includes('/password');
      if (error.status === 401 && !isAuthCall) {
        localStorage.removeItem(TOKEN_NAME);
        router.navigate(['login']);
      } else if (error.status === 403) {
        snackBar.open('You do not have permission to perform this action.', 'Dismiss', {
          panelClass: ['snack-error'],
        });
      }
      return throwError(() => error);
    }),
  );
};
